/**
 * dataTransformers.js
 * Generic data transformation functions for vaccination module
 * Extracted from original vaccinationutils.js for better organization
 */

import dayjs from 'dayjs';

// ================= DATA TRANSFORMATION FUNCTIONS =================

/**
 * Transform village API response to standardized format
 * @param {Object} apiData - API response data
 * @returns {Array} - Transformed village array
 */
export const transformVillageData = (apiData) => {
  if (!apiData?.data?.villageData) return [];
  return apiData.data.villageData.map(village => ({
    id: village.village_id,
    name: village.village_name
  }));
};

/**
 * Transform health village API response to standardized format
 * @param {Object} apiData - API response data
 * @returns {Array} - Transformed health village array
 */
export const transformHealthVillageData = (apiData) => {
  if (!apiData?.data?.healthVillageData) return [];
  return apiData.data.healthVillageData.map(village => ({
    id: village.village_id,
    name: village.village_name
  }));
};

/**
 * Transform ward API response to standardized format
 * @param {Object} apiData - API response data
 * @returns {Array} - Transformed ward array
 */
export const transformWardData = (apiData) => {
  if (!apiData?.data?.wardData) return [];
  return apiData.data.wardData.map(ward => ({
    id: ward.ward_id,
    name: ward.ward_name
  }));
};

/**
 * Transform health ward API response to standardized format
 * @param {Object} apiData - API response data
 * @returns {Array} - Transformed health ward array
 */
export const transformHealthWardData = (apiData) => {
  const data = apiData?.data?.healthWardData || apiData?.data?.health_wardData || [];
  return data.map(ward => ({
    id:  ward.add_ward_id,
    name: ward.add_ward_name
  }));
};

/**
 * Transform vaccine materials API response to standardized format.
 *
 * The `material_id` field returned by the materials API is an OpenSSL-encrypted
 * string.  We decrypt it once here so that every downstream consumer (including
 * transformVaccinationData) works with a stable plain-integer key rather than
 * opaque ciphertext.  This follows the same pattern used in Uwinsession.jsx where
 * decryptOpenSSLAES is called during data normalisation before any comparisons.
 *
 * @param {Object}   materialsData    - Materials API response
 * @param {Function} decryptFunction  - decryptOpenSSLAES (or equivalent) for material_id
 * @returns {Array} - Transformed vaccine array with decrypted integer `id` fields
 */
export const transformVaccineData = (materialsData, decryptFunction) => {
  if (!materialsData?.data || !Array.isArray(materialsData.data)) return [];

  const vaccineMap = new Map();

  materialsData.data.forEach(material => {
    // Decrypt the material_id once at transformation time.
    // parseInt normalises the decrypted string to a number so comparisons are
    // type-safe (avoids "123" !== 123 surprises later).
    // If decryptFunction is not provided we fall back to the raw value so the
    // function remains backward-compatible during any gradual migration.
    const rawId = material.material_id;
    const vaccineId = decryptFunction
      ? parseInt(decryptFunction(rawId), 10)
      : rawId;
    const vaccineName = material.material_name;

    // De-duplicate: the materials API may return multiple rows per vaccine
    // (one per dose).  We only need one entry per unique decrypted ID.
    if (!vaccineMap.has(vaccineId)) {
      const vaccineEntry = {
        id: vaccineId,          // plain integer - safe to compare with ===
        name: vaccineName,
        selected: false,
        category: material.material_category,
        type: material.material_type_name,
        ageRange: `${material.display_min_age} - ${material.display_max_age}`,
        doses: material.no_of_doses,
        priority: material.priority,
        displayDoseNames: material.display_dose_names || [],
        doseFrequencies: material.dose_frequencies || []
      };

      vaccineMap.set(vaccineId, vaccineEntry);
    }
  });

  return Array.from(vaccineMap.values()).sort((a, b) => a.priority - b.priority);
};

/**
 * Transform ASHA users API response to standardized format
 * @param {Array} ashaResponse - ASHA API response
 * @param {Function} decryptFunction - Decryption function for mobile numbers
 * @returns {Array} - Transformed ASHA users array
 */
export const transformAshaData = (ashaResponse, decryptFunction) => {
  if (!Array.isArray(ashaResponse)) return [];

  return ashaResponse.map(asha => ({
    id: asha.user_id,
    name: asha.display_name,
    mobileNumber: decryptFunction(asha.mobile_number),
  }));
};

/**
 * Generate dose options for a vaccine based on eligibility.
 * Includes both primary doses AND precaution/booster doses (if any).
 *
 * Precaution doses are appended after primary doses with a continuing doseIndex
 * (e.g., OPV: indices 0,1,2 = primary; index 3 = booster).
 * This matches what the rule engines expect — they detect isPrecaution via
 *   doseIndex >= material.no_of_doses
 *
 * @param {Object} vaccineEligibility - Vaccine eligibility data from API
 * @param {Object} vaccineInfo        - Additional vaccine info from transformVaccineData
 * @param {Object} [rawMaterial]      - Full raw material object from all-uip-materials API
 * @returns {Array} - Array of dose options (primary + precaution)
 */
export const generateDoseOptions = (vaccineEligibility, vaccineInfo, rawMaterial = null) => {
  const doses = [];
  const numDoses = vaccineEligibility.no_of_doses || 1;
  const displayDoseNames = vaccineInfo?.displayDoseNames || [];

  // ---- Primary doses ----
  for (let i = 0; i < numDoses; i++) {
    doses.push({
      doseNumber: i + 1,
      doseName: displayDoseNames[i],
      eligible: true,
      status: i === 0 ? 'Eligible' : 'Upcoming',
      selected: false,
      administeredDate: null,
      isPrecaution: false
    });
  }

  // ---- Precaution / Booster doses ----
  // Source: display_precaution_dose_names from the raw material object.
  // We do NOT use no_of_precaution_doses from vaccineEligibility because the
  // eligibility API response does not always carry it. The raw material is the
  // authoritative source for precaution metadata.
  const precautionNames = rawMaterial?.display_precaution_dose_names || [];
  const numPrecaution = precautionNames.length;

  for (let i = 0; i < numPrecaution; i++) {
    doses.push({
      // doseNumber continues from primary (1-based, e.g., booster = 4 for OPV)
      doseNumber: numDoses + i + 1,
      doseName: precautionNames[i] || `Booster ${i + 1}`,
      eligible: true,
      status: 'Upcoming',
      selected: false,
      administeredDate: null,
      isPrecaution: true  // Flag for UI to optionally style differently
    });
  }

  return doses;
};

/**
 * Transform vaccination eligibility data combining with vaccine info.
 *
 * Both the materials API (allUipMaterials) and the eligibility APIs
 * (eligibleMaterialsCurrentVaccinations / eligibleMaterialsPrevVaccinations)
 * encrypt `material_id` independently with OpenSSL AES, so the same logical
 * integer produces a different ciphertext in each response.  Comparing raw
 * encrypted strings therefore never matches.
 *
 * The solution (matching the Uwinsession.jsx pattern) is to decrypt both sides
 * before comparison.  Since transformVaccineData has already stored decrypted
 * integers in `allVaccines[].id`, we only need to decrypt the eligibility-side
 * `material_id` here, then compare the two plain integers with ===.
 *
 * IMPORTANT: allUipMaterialsRaw is the raw array from the all-uip-materials API.
 * It is used to attach the full material object (with age limits, dose_frequencies,
 * precaution arrays etc.) onto each vaccine so the rule engines in
 * ChildCurrentRules / ChildPreviousRules can access vaccine.material correctly.
 * Without this, vaccine.material is always undefined and the rules fall back to
 * the permissive DOB→today window, ignoring all age limit constraints.
 *
 * @param {Array|Object} vaccinationData      - Vaccination eligibility API response
 * @param {Array}        allVaccines          - Transformed vaccine list from transformVaccineData
 *                                             (contains decrypted integer `id` fields)
 * @param {Function}     decryptFunction      - decryptOpenSSLAES (or equivalent)
 * @param {Array}        [allUipMaterialsRaw] - Raw array from the all-uip-materials API
 *                                             (optional but required for age-limit enforcement)
 * @returns {Array} - Transformed vaccination data with dose options
 */
export const transformVaccinationData = (vaccinationData, allVaccines, decryptFunction, allUipMaterialsRaw = []) => {
  let dataArray = [];
  if (Array.isArray(vaccinationData)) {
    dataArray = vaccinationData;
  } else if (vaccinationData?.data && Array.isArray(vaccinationData.data)) {
    dataArray = vaccinationData.data;
  } else {
    return [];
  }

  return dataArray.map(vaccineEligibility => {
    // Decrypt the eligibility-side material_id to get a plain integer that can
    // be compared against the already-decrypted ids stored in allVaccines.
    // If decryptFunction is absent we fall back to the raw value so the function
    // degrades gracefully (backward-compatible with call sites not yet updated).
    let decryptedEligibilityId;
    try {
      decryptedEligibilityId = decryptFunction
        ? parseInt(decryptFunction(vaccineEligibility.material_id), 10)
        : vaccineEligibility.material_id;
    } catch (e) {
      // A single bad record should not abort the whole list.
      console.warn(
        'transformVaccinationData: failed to decrypt material_id',
        vaccineEligibility.material_id,
        e
      );
      decryptedEligibilityId = vaccineEligibility.material_id;
    }

    // ID-based lookup: both sides are now plain integers, so === is reliable.
    const vaccineInfo = allVaccines.find(v => v.id === decryptedEligibilityId) || {};

    // Find the full raw material from the allUipMaterialsRaw API response.
    // This is the complete object with lower_age_limits, upper_age_limits,
    // dose_frequencies, precaution arrays, etc. that the rule engines need.
    // We match by material_name since the encrypted IDs differ between responses.
    const rawMaterial = Array.isArray(allUipMaterialsRaw)
      ? allUipMaterialsRaw.find(m => m.material_name === vaccineEligibility.material_name) || null
      : null;

    return {
      // Store the decrypted integer so payload builders and comparisons
      // downstream always work with the plain value.
      id: decryptedEligibilityId,
      name: vaccineEligibility.material_name,
      selected: vaccineEligibility.keep || false,
      keep: vaccineEligibility.keep,
      no_of_doses: vaccineEligibility.no_of_doses,
      ...vaccineInfo,
      // Attach the full raw material so ChildCurrentRules / ChildPreviousRules
      // can read vaccine.material.lower_age_limits, upper_age_limits, etc.
      // Without this, vaccine.material is undefined and all age constraints are ignored.
      material: rawMaterial,
      selectedDose: null,       // For current vaccinations (radio button selection)
      availableDoses: generateDoseOptions(vaccineEligibility, vaccineInfo, rawMaterial)
    };
  });
};

// ================= HELPER FUNCTIONS =================

/**
 * Get village name by ID from villages array
 * @param {string} villageId - Village ID
 * @param {Array} villages - Villages array
 * @returns {string} - Village name
 */
export const getVillageName = (villageId, villages) => {
  const village = villages.find(v => v.id === villageId);
  return village?.name || "";
};

/**
 * Get health village name by ID from health villages array
 * @param {string} healthVillageId - Health village ID
 * @param {Array} healthVillages - Health villages array
 * @returns {string} - Health village name
 */
export const getHealthVillageName = (healthVillageId, healthVillages) => {
  const healthVillage = healthVillages.find(v => v.id === healthVillageId);
  return healthVillage?.name || "";
};

/**
 * Get ward name by ID from wards array
 * @param {string} wardId - Ward ID
 * @param {Array} wards - Wards array
 * @returns {string} - Ward name
 */
export const getWardName = (wardId, wards) => {
  const ward = wards.find(w => w.id === wardId);
  return ward?.name || "";
};

/**
 * Get health ward name by ID from health wards array
 * @param {string} healthWardId - Health Ward ID
 * @param {Array} healthWards - Health Wards array
 * @returns {string} - Health ward name
 */
export const getHealthWardName = (healthWardId, healthWards) => {
  const healthWard = healthWards.find(w => w.id === healthWardId);
  return healthWard?.name || "";
};

/**
 * Extract dose number from dose name
 * @param {string} doseName - Dose name (e.g., "RVV-1", "Td-Booster")
 * @param {Array} displayDoseNames - Array of display dose names
 * @returns {number} - Extracted dose number
 */
export const getDoseNumber = (doseName, displayDoseNames) => {
  const doseIndex = displayDoseNames.findIndex(name => name === doseName);
  if (doseIndex !== -1) {
    return doseIndex + 1;
  }
  const match = doseName.match(/-(\d+)$/);
  if (match) {
    return parseInt(match[1], 10);
  }
  console.warn(`Could not extract dose number from "${doseName}", defaulting to 1`);
  return 1;
};

// ================= VALIDATION FUNCTIONS =================

/**
 * Validate current vaccination selections
 * @param {Array} currentVaccines - Current vaccines array
 * @returns {Object} - Validation result with isValid and message
 */
export const validateCurrentVaccinations = (currentVaccines) => {
  const selectedVaccines = currentVaccines.filter(vaccine => vaccine.selected);

  // No vaccines checked at all
  if (selectedVaccines.length === 0) {
    return {
      isValid: false,
      message: "Please select at least one vaccine with a dose"
    };
  }

  // Every checked vaccine must also have a dose selected.
  // A checked checkbox alone is not a valid selection.
  const vaccineWithoutDose = selectedVaccines.find(vaccine => !vaccine.selectedDose);
  if (vaccineWithoutDose) {
    return {
      isValid: false,
      message: "Please select a dose for the selected vaccine"
    };
  }

  return { isValid: true, message: "" };
};

/**
 * Validate previous vaccination selections
 * @param {Array} previousVaccines - Previous vaccines array
 * @returns {Object} - Validation result with isValid and message
 */
export const validatePreviousVaccinations = (previousVaccines) => {
  const selectedVaccines = previousVaccines.filter(vaccine => vaccine.selected);

  // No vaccines checked at all
  if (selectedVaccines.length === 0) {
    return {
      isValid: false,
      message: "Please select at least one previous vaccination dose"
    };
  }

  // Every checked vaccine must have at least one dose checkbox ticked.
  // Checking the vaccine row alone without selecting a dose is not valid.
  const vaccineWithoutDose = selectedVaccines.find(
    vaccine => !vaccine.availableDoses || !vaccine.availableDoses.some(dose => dose.selected)
  );
  if (vaccineWithoutDose) {
    return {
      isValid: false,
      message: "Please select a dose for the selected vaccine"
    };
  }

  // MR-1 requires an administered date; other doses may be selected without a date.
  for (const vaccine of selectedVaccines) {
    if (!vaccine.availableDoses) {
      continue;
    }

    const mr1Dose = vaccine.availableDoses.find(
      (dose) => dose.selected && dose.doseName === 'MR-1'
    );

    if (mr1Dose && !mr1Dose.administeredDate) {
      return {
        isValid: false,
        message: 'Please enter vaccination date for MR-1',
      };
    }
  }

  return { isValid: true, message: "" };
};

// ================= FORM DATA PREPARATION =================

/**
 * Prepare form data with additional metadata for API submission
 * @param {Object} values - Formik form values
 * @param {number} currentTab - Current tab index (0 or 1)
 * @param {Array} villages - Villages array
 * @param {Array} healthVillages - Health villages array
 * @param {Array} wards - Wards array
 * @param {Array} healthWards - Health wards array
 * @param {Object} beneficiaryData - Beneficiary data
 * @returns {Object} - Prepared form data with metadata
 */
export const prepareFormDataWithMetadata = (values, currentTab, villages, healthVillages, wards, healthWards, beneficiaryData) => {
  const activeVaccines = currentTab === 0 ?
    (values.current_vaccines || []) :
    (values.previous_vaccines || []);

  return {
    ...values,
    currentTab,
    vaccinationType: currentTab === 0 ? 'current' : 'previous',
    selectedVaccines: activeVaccines.filter(vaccine => vaccine.selected),
    // Add helper data for centralized API
    villages,
    healthVillages,
    wards,
    healthWards,
    beneficiaryData
  };
};

/**
 * Get initial form values for Formik
 * @param {Array} currentVaccinations - Current vaccinations array
 * @param {Array} previousVaccinations - Previous vaccinations array
 * @returns {Object} - Initial form values
 */
export const getInitialFormValues = (currentVaccinations = [], previousVaccinations = []) => {
  // NOTE: location fields (lgd_village, health_village, lgd_ward, health_ward,
  // asha_user_id, tag_to_session_site) are intentionally NOT included here.
  // They are owned by the OUTER Formik in MemberVaccination.jsx and injected
  // into VaccinationContainer via the locationFormValues prop at submit time.
  // Defining them here causes the inner form's empty strings to overwrite
  // the user's dropdown selections during the { ...locationFormValues, ...values }
  // merge in handleSubmit.
  return {
    select_all_vaccines: false,
    current_vaccines: [...currentVaccinations],
    previous_vaccines: [...previousVaccinations],
  };
};

// ================= BENEFICIARY TYPE DETECTION =================
// Moved from rulesAdapter.js as part of Step 3: Remove redundant vaccinationUtils

/**
 * Determine beneficiary type based on available data
 * @param {Object} beneficiaryData - Beneficiary data object
 * @returns {string} - 'PW' or 'CHILD'
 */
export const getBeneficiaryType = (beneficiaryData) => {
  // This logic would need to be implemented based on how the system identifies PW vs Child
  // For now, we can check for presence of LMC date vs DOB
  if (beneficiaryData?.lmc_date) {
    return 'PW';
  } else if (beneficiaryData?.dob) {
    return 'CHILD';
  } else {
    // CRITICAL: Throw error instead of silent fallback to prevent data corruption
    console.error('❌ Unable to determine beneficiary type from data:', beneficiaryData);
    throw new Error('Unable to determine beneficiary type - missing LMC date and DOB');
  }
};

/**
 * Extract profile data based on beneficiary type
 * @param {Object} beneficiaryProfile - Full beneficiary profile from API
 * @param {string} beneficiaryType - 'PW' or 'CHILD'
 * @returns {Object} - Extracted profile data relevant to the type
 */
export const extractProfileData = (beneficiaryProfile, beneficiaryType) => {
  if (beneficiaryType === 'PW') {
    return {
      lmc_date: beneficiaryProfile?.lmc_date,
      vaccination: beneficiaryProfile?.vaccination
    };
  } else if (beneficiaryType === 'CHILD') {
    return {
      dob: beneficiaryProfile?.dob
    };
  }
  return {};
};